import { motion, useMotionValue, useScroll, useSpring, useTransform, type MotionValue } from "framer-motion";
import { ArrowLeft, BadgeCheck } from "lucide-react";
import { useRef, type CSSProperties, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import cn from "~/utils/cn";
import { formatNumber } from "../../plans";
import Button from "../../ui/Button";
import type { LandingData } from "../types";
import Mark3D from "./Mark3D";

// Sample story text placed around the mark, each in a different font like IG text stickers. Decorative (aria-hidden).
// depth: how far it drifts with scroll and pointer, so nearer stickers move more.
const STICKERS = [
  { text: "سلام!", r: -9, depth: 1, pos: "top-[5%] start-[6%] md:top-[7%] md:start-[8%]", cls: "font-alt text-[length:clamp(1.5rem,1rem+2vw,2.25rem)] leading-[1.35] [font-variation-settings:'wght'_800,'wdth'_100]" },
  { text: "امروز", r: 7, depth: 0.6, pos: "top-[13%] end-[5%] md:top-[16%] md:end-0", cls: "rounded-md bg-accent px-[.45em] font-display text-[length:clamp(1.05rem,.8rem+1vw,1.4rem)] font-extrabold leading-[1.5] text-on-accent" },
  { text: "حس خوب", r: -5, depth: 1.3, pos: "bottom-[12%] start-[4%] md:bottom-[16%] md:start-0", cls: "font-alt text-[length:clamp(2rem,1.3rem+2.6vw,3.1rem)] leading-[1.2] [font-variation-settings:'wght'_300,'wdth'_70]" },
  { text: "جمعه‌ها", r: 9, depth: 0.8, pos: "bottom-[3%] end-[12%] md:bottom-[5%] md:end-[14%]", cls: "rounded-md bg-[color-mix(in_srgb,var(--c-bg)_80%,transparent)] px-[.5em] font-display text-[length:clamp(1rem,.8rem+.8vw,1.3rem)] font-black leading-[1.6]" },
  { text: "تولدت مبارک", r: -4, depth: 0.45, pos: "hidden md:block md:top-[46%] md:end-[-4%]", cls: "font-body text-[length:clamp(1.3rem,1rem+1vw,1.7rem)] font-extralight leading-[1.4]" },
];

// CSS entrance, so it plays from the server HTML; d orders the pieces in reading order.
const rise = "motion-safe:animate-[c-rise_.9s_var(--ease-out)_backwards]";
const delay = (d: number): CSSProperties => ({ animationDelay: `${d * 75 + 60}ms` });

export default function HeroC({ data, id = "hero" }: { data: Pick<LandingData, "proDaysLeft">; id?: string }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  // pointer position over the hero, -0.5..0.5 (mouse only; touch has no hover to follow)
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const sx = useSpring(px, { stiffness: 90, damping: 18, mass: 0.6 });
  const sy = useSpring(py, { stiffness: 90, damping: 18, mass: 0.6 });

  const onPointerMove = (e: PointerEvent<HTMLElement>) => {
    if (e.pointerType !== "mouse") return;
    const r = e.currentTarget.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width - 0.5);
    py.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onPointerLeave = () => (px.set(0), py.set(0));

  const title = t("ui.landing.hero.title");
  const emphasis = t("ui.landing.hero.titleEmphasis");
  const [before = title, after = ""] = title.includes(emphasis) ? title.split(emphasis) : [title];
  const words = [...before.split(" ").filter(Boolean).map((w) => ({ w, mark: false })), ...(title.includes(emphasis) ? [{ w: emphasis, mark: true }] : []), ...after.split(" ").filter(Boolean).map((w) => ({ w, mark: false }))];
  const pro = data.proDaysLeft !== null;

  return (
    <section
      ref={ref}
      id={id}
      aria-labelledby={`${id}-title`}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      className="relative mx-auto grid w-full max-w-[1200px] content-start gap-1 px-[var(--gutter)] pb-10 md:min-h-[calc(100dvh-var(--nav-h))] md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] md:content-center md:items-center md:gap-6 md:pb-14"
    >
      <div className="relative z-[3] [container-type:inline-size]">
        <h1 id={`${id}-title`} className="mb-4 text-balance font-display text-[length:clamp(1.9rem,9.4cqi,3.5rem)] font-black leading-[1.32] text-ink">
          {words.map(({ w, mark }, i) => (
            <span key={i}>
              <span className={cn("inline-block", rise)} style={delay(i)}>
                {mark ? <mark className="inline-block -rotate-2 rounded-md bg-accent px-[.2em] pb-[.04em] leading-[1.25] text-on-accent">{w}</mark> : w}
              </span>{" "}
            </span>
          ))}
        </h1>
        <p className={cn("mb-6 max-w-[36ch] text-[17px] leading-[1.9] text-ink md:text-lg", rise)} style={delay(words.length)}>
          {t("ui.landing.hero.subtitle")}
        </p>
        {pro && (
          <p className={cn("mb-4 inline-flex items-center gap-2 font-display text-[15px] font-bold leading-relaxed text-success", rise)} style={delay(words.length + 1)}>
            <BadgeCheck className="size-5 shrink-0" strokeWidth={1.75} aria-hidden />
            {t("ui.landing.hero.proUntil", { days: formatNumber(data.proDaysLeft!) })}
          </p>
        )}
        <div className={rise} style={delay(words.length + (pro ? 2 : 1))}>
          <Button
            href="#plans"
            size="lg"
            className="group font-display text-[17px] font-extrabold shadow-[inset_0_-4px_0_rgba(32,76,165,.14),0_14px_30px_-12px_rgba(5,9,45,.75)] hover:-translate-y-0.5"
          >
            {pro ? t("ui.landing.hero.ctaRenew") : t("ui.landing.hero.cta")}
            <ArrowLeft className="size-5 transition-transform duration-300 ease-out group-hover:-translate-x-1 ltr:rotate-180" strokeWidth={1.75} aria-hidden />
          </Button>
        </div>
      </div>

      <div
        aria-hidden
        className="relative -order-1 -mx-[var(--gutter)] h-[clamp(270px,40svh,360px)] max-md:[@media(max-height:700px)]:h-[clamp(200px,34svh,300px)] md:order-none md:mx-0 md:h-[min(74svh,620px)]"
      >
        {/* light pool behind the stem, so the lit bands read against the darker blue */}
        <div className="absolute inset-[8%_10%_-6%_6%] translate-x-[-4%] translate-y-[14%] bg-[radial-gradient(closest-side,rgba(69,129,233,.85),rgba(69,129,233,0))] md:inset-[6%_4%_0_0] md:translate-x-[-10%]" />
        <Mark3D progress={scrollYProgress} px={px} py={py} className="absolute inset-0" />
        {STICKERS.map((s, i) => (
          <Sticker key={s.text} {...s} i={i} progress={scrollYProgress} sx={sx} sy={sy} />
        ))}
      </div>
    </section>
  );
}

// Three layers so scroll drift, pointer drift and the resting tilt never fight over one transform.
function Sticker({ text, r, depth, pos, cls, i, progress, sx, sy }: (typeof STICKERS)[number] & { i: number; progress: MotionValue<number>; sx: MotionValue<number>; sy: MotionValue<number> }) {
  const y = useTransform(progress, [0, 1], [0, -depth * 150]);
  const x2 = useTransform(sx, (v) => v * depth * 28);
  const y2 = useTransform(sy, (v) => v * depth * 20);
  return (
    <motion.span style={{ y }} className={cn("absolute z-[2] motion-reduce:!transform-none", pos)}>
      <motion.span style={{ x: x2, y: y2 }} className="block motion-reduce:!transform-none">
        <span
          className={cn("inline-block whitespace-nowrap text-ink [transform:rotate(var(--r))] motion-safe:animate-[c-pop_.9s_var(--ease-spring)_backwards]", cls)}
          style={{ "--r": `${r}deg`, animationDelay: `${i * 90 + 380}ms` } as CSSProperties}
        >
          {text}
        </span>
      </motion.span>
    </motion.span>
  );
}
